import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useHistory } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useDispatch, useSelector } from 'react-redux'
import { openErrorToaster } from 'helpers/toaster.helpers'
import { createWithdrawRequest } from 'redux-thunk/thunk/user.thunk'
import ErrorMessage from 'components/layout/ErrorMessage'
import { LeftArrowIcon } from 'components/ui-kit/Icons/svg/index'
import CryptoLogo from 'components/ui-kit/CryptoLogo'
import { blockInvalidChar, cryptoToFiat, formatPrice, getPrecision } from 'utils/helperFunctions.utils'
import { getWalletAddressValidationError } from 'utils/walletAddress.utils'
import { CRYPTO_COINS, LOADER_HANDLER_TYPES, ROUTE_PATHS } from 'constants/index'
import { setShowCashierPopup } from 'redux-thunk/redux/slices/settings.slice'
import Loader from 'components/ui-kit/Loader/index'
import WithdrawTransactionPasswordModal from './WithdrawTransactionPasswordModal'
import WithdrawConfirmModal from './WithdrawConfirmModal'

// Double background (padding-box fill + border-box gradient) paints the gold
// border. Keep each arbitrary class as one unbroken literal for Tailwind.
const GOLD_INPUT =
  'relative mb-4 box-border w-full overflow-hidden rounded-xl border border-solid border-transparent ' +
  'px-4 py-4 text-base text-white shadow-none outline-none md:px-6 md:py-5 ' +
  'shadow-[inset_0_1px_0_rgb(var(--pm-gold)/0.06)] ' +
  '[background:linear-gradient(145deg,#472e00,#090b0b_25%)_padding-box,linear-gradient(135deg,#8a6500,#ffd700,#b8860b,#b8860b,#8a6500)_border-box] ' +
  'placeholder:text-[var(--gray-20)] focus:border-transparent focus:shadow-none'

const FORM_INPUT =
  'h-12 w-full box-border rounded-[0.625rem] ' +
  'bg-[var(--gray-90)] py-[0.438rem] pl-10 pr-3 text-base text-white shadow-none outline-none ' +
  'placeholder:text-[var(--gray-20)] border-[#f7b731] focus:border-[#f7b731] focus:bg-[var(--gray-90)] focus:text-white focus:shadow-none'

const BTN_SECONDARY =
  'inline-flex min-h-11 min-w-[120px] cursor-pointer items-center justify-center whitespace-nowrap ' +
  'rounded-lg border-0 bg-[linear-gradient(#EFD43C,#CB7D00)] px-6 py-1 text-base font-medium ' +
  'leading-[1.688rem] text-white shadow-none transition-all duration-300 ' +
  'hover:bg-[linear-gradient(#EFD43C,#CB7D00)] focus:bg-[linear-gradient(#EFD43C,#CB7D00)] ' +
  'active:scale-[0.98] active:opacity-80 disabled:bg-[linear-gradient(#EFD43C,#CB7D00)] ' +
  'disabled:text-white disabled:opacity-50 max-lg:text-sm max-md:min-h-10 max-md:min-w-[106px] ' +
  'max-md:text-base max-sm:min-h-8 max-sm:min-w-0 max-sm:px-3 max-sm:py-2 max-sm:text-sm max-sm:leading-4'

const WithdrawCurrencySection = ({ selectedWithdrawWallet, setSelectedWithdrawWallet }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const { selectedWallet, user } = useSelector(state => state.user)
  const { [LOADER_HANDLER_TYPES.submit]: loading } = useSelector(state => state.loader)
  const [fiatAmount, setFiatAmount] = useState(0)
  const [fiatFocused, setFiatFocused] = useState(false)
  const [headerImgError, setHeaderImgError] = useState(false)
  const [cryptoImgError, setCryptoImgError] = useState(false)
  const [maxCryptoAmount, setMaxCryptoAmount] = useState(null)
  const [pendingWithdraw, setPendingWithdraw] = useState(null)
  const [transactionPassword, setTransactionPassword] = useState('')
  const [showPinModal, setShowPinModal] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const PRIMARY_FIAT = 'USD'
  const MIN_WITHDRAW_AMOUNT = 0.00001
  const currencyCode = selectedWithdrawWallet?.currency?.code || selectedWithdrawWallet?.currency?.name || ''
  const currencyName = selectedWithdrawWallet?.currency?.name || currencyCode
  const isUsdt = currencyCode.toUpperCase().includes('USDT') || currencyName.toUpperCase().includes('TETHER')
  const formattedMaxAmount = maxCryptoAmount == null
    ? '—'
    : formatPrice(getPrecision(maxCryptoAmount, 8), false, 8)
  const maxUsdAmount = Number(selectedWallet?.amount) || 0
  const formattedMaxUsdAmount = formatPrice(getPrecision(maxUsdAmount, 8), false, 8)
  const fiatExceedsMax = fiatAmount !== '' && fiatAmount != null && Number(fiatAmount) > maxUsdAmount
  const fiatMaxErrorMessage = t('withdrawExceedsMax', {
    max: formattedMaxUsdAmount,
    currency: PRIMARY_FIAT
  })

  const getAddressValidationMessage = useCallback((value) => {
    const addressError = getWalletAddressValidationError(value, currencyCode)
    if (!addressError) return null

    const interpolation = {
      currency: currencyName,
      expected: addressError.expected,
      actual: addressError.actual
    }

    return addressError.type === 'length'
      ? t('invalidWalletAddressLength', interpolation)
      : t('invalidWalletAddressFormat', interpolation)
  }, [currencyCode, currencyName, t])

  const schema = useMemo(() => yup.object().shape({
    amount: yup
      .number()
      .typeError(t('amountIsRequired'))
      .required(`${t('amountIsRequired')}`)
      .moreThan(
        MIN_WITHDRAW_AMOUNT,
        t('minWithdrawAmount', { min: MIN_WITHDRAW_AMOUNT })
      ),
    address: yup
      .string()
      .trim()
      .required(t('addressIsRequired'))
      .test('not-equal', t('withdrawAddressCannotBeSame'), (value) => {
        return value !== selectedWithdrawWallet?.walletAddress
      })
      .test('wallet-address-length', function (value) {
        if (!value) return true
        const message = getAddressValidationMessage(value)
        return message ? this.createError({ message }) : true
      })
  }), [getAddressValidationMessage, selectedWithdrawWallet?.walletAddress, t])

  const {
    handleSubmit,
    register,
    watch,
    formState: { errors },
    setValue
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'all'
  })
  const watchCryptoAmount = watch('amount', 0)

  const handleChangeFiat = (e) => {
    const val = e.target.value
    setFiatAmount(val)
    const coinCode = CRYPTO_COINS[selectedWithdrawWallet?.currency?.code] || selectedWithdrawWallet?.currency?.code || 'BTC'
    cryptoToFiat(PRIMARY_FIAT, coinCode, val)
      .then((res) => {
        setValue('amount', !res ? '' : String(res), {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true
        })
      })
      .catch((e) => {
        console.log(e)
      })
  }

  const handleWithdrawRequest = async (values) => {
    const withdrawalAddress = values.address?.trim()
    const addressErrorMessage = getAddressValidationMessage(withdrawalAddress)
    if (addressErrorMessage) {
      openErrorToaster({ message: addressErrorMessage })
      return
    }

    const withdrawalAmount = parseFloat(values.amount)
    if (fiatExceedsMax || (maxCryptoAmount != null && withdrawalAmount > maxCryptoAmount)) {
      openErrorToaster({ message: fiatMaxErrorMessage })
      return
    }

    if (withdrawalAmount < MIN_WITHDRAW_AMOUNT) {
      openErrorToaster({
        message: t('withdrawMinAmount', { min: MIN_WITHDRAW_AMOUNT })
      })
      return
    }

    setPendingWithdraw({
      withdrawalAmount,
      walletId: parseInt(selectedWithdrawWallet.id),
      withdrawalAddress
    })
    setShowPinModal(true)
  }

  const handlePinVerified = (pin) => {
    setTransactionPassword(pin)
    setShowPinModal(false)
    setShowConfirmModal(true)
  }

  const handleGoToSettings = (path) => {
    setShowPinModal(false)
    dispatch(setShowCashierPopup(false))
    history.push(path)
  }

  const handleConfirmWithdraw = () => {
    if (!pendingWithdraw || !transactionPassword) return
    dispatch(createWithdrawRequest({
      params: {
        ...pendingWithdraw,
        transactionPassword
      }
    }))
  }

  useEffect(() => {
    const usdBalance = Number(selectedWallet?.amount) || 0
    const coinCode = CRYPTO_COINS[selectedWithdrawWallet?.currency?.code] || selectedWithdrawWallet?.currency?.code || 'BTC'
    if (!usdBalance) {
      setMaxCryptoAmount(0)
      return
    }
    cryptoToFiat(PRIMARY_FIAT, coinCode, usdBalance)
      .then((res) => {
        setMaxCryptoAmount(!res ? 0 : Number(res))
      })
      .catch(() => {
        setMaxCryptoAmount(0)
      })
  }, [selectedWallet?.amount, selectedWithdrawWallet?.currency?.code])

  useEffect(() => {
    if (!fiatFocused) {
      const coinCode = CRYPTO_COINS[selectedWithdrawWallet?.currency?.code] || selectedWithdrawWallet?.currency?.code || 'BTC'
      cryptoToFiat(
        coinCode,
        PRIMARY_FIAT,
        watchCryptoAmount
      )
        .then((res) => {
          setFiatAmount(!res ? 0 : res)
        })
        .catch((e) => {
          console.log(e)
        })
    }
  }, [watchCryptoAmount])

  return (
    <div>
      <div className='mb-6 flex items-center max-md:mb-4 max-md:flex-col max-md:items-start max-md:gap-y-4'>
        <h3 className='yb-0 flex items-center text-xl font-bold uppercase text-white max-md:text-base'>
          <NavLink
            to='#'
            className='p-0 [&_svg]:mr-4 [&_svg]:h-4 [&_svg]:w-4 [&_svg]:brightness-[10]'
            onClick={() => {
              setSelectedWithdrawWallet(null)
            }}
          >
            <LeftArrowIcon />
          </NavLink>
          <div className='mr-2.5 inline-flex h-[30px] w-[30px] items-center justify-center rounded-full [&_img]:h-full [&_img]:object-cover'>
            {selectedWithdrawWallet?.currency?.image && !headerImgError && !isUsdt
              ? (
                <img
                  src={selectedWithdrawWallet?.currency?.image}
                  alt='Currency'
                  onError={() => setHeaderImgError(true)}
                />
                )
              : (
                <CryptoLogo code={selectedWithdrawWallet?.currency?.code} name={selectedWithdrawWallet?.currency?.name} size={24} />
                )}
          </div>
          {t('withdrawCurrency', { currency: selectedWithdrawWallet?.currency?.name })}
        </h3>
        <NavLink
          to={ROUTE_PATHS.WITHDRAWALS}
          className='pl-4 text-base font-medium leading-none text-[var(--blue-300)] underline'
          onClick={() => {
            dispatch(setShowCashierPopup(false))
          }}
        >
          {t('viewTransactions')}
        </NavLink>
      </div>
      <form onSubmit={handleSubmit(handleWithdrawRequest)}>
        <div className='mb-2 rounded-[0.625rem] p-6 text-[var(--gray-20)] max-md:p-4'>
          <div className='flex'>
            <p className='mb-0 pr-8 text-base font-normal leading-relaxed text-[var(--gray-20)]'>
              {t('enterAddressToReceiveFunds', {
                currency: selectedWithdrawWallet?.currency?.name
              })}
            </p>
          </div>
          <div>
            <div className='relative mt-4 w-full'>
              <label htmlFor='address' className='mb-2 block text-sm max-md:text-xs'>
                {t('receivingCurrencyAddress', {
                  currency: selectedWithdrawWallet?.currency?.name
                })}
                <span className='text-[var(--red)]'>*</span>
              </label>
              <input
                type='text'
                className={`${GOLD_INPUT} ${errors?.address ? 'border-[var(--red)]' : ''}`}
                name='address'
                id='address'
                {...register('address')}
                placeholder={t('pasteYourCurrencyAddressHere', {
                  currency: selectedWithdrawWallet?.currency?.name
                })}
              />
              {errors && errors.address && (
                <ErrorMessage
                  className='ml-2 flex text-[var(--red)]'
                  message={errors.address.message}
                />
              )}
            </div>
          </div>
        </div>
        <div className='mb-2 rounded-[0.625rem] p-6 text-[var(--gray-20)] max-md:p-4'>
          <div>
            <label htmlFor='amount' className='mb-2 block text-sm max-md:text-xs'>
              {t('withdrawalAmount')}
              <span className='p-0 text-[var(--red)]'>*</span>
            </label>
            <p className='mb-3 text-xs font-medium normal-case text-[var(--gray-20)]'>
              {t('maxWithdrawAmountLabel', {
                max: formattedMaxAmount,
                currency: selectedWithdrawWallet?.currency?.code || currencyName
              })}
            </p>

            <div className='md:flex md:items-start md:justify-center'>
              <div className='w-full'>
                <div className='relative'>
                  <input
                    type='number'
                    className={`${FORM_INPUT} ${fiatExceedsMax ? 'border-[var(--red)]' : ''}`}
                    id='newUser'
                    value={fiatAmount}
                    placeholder={t('topHeaderAmount')}
                    onChange={handleChangeFiat}
                    onKeyDown={blockInvalidChar}
                    onFocus={() => {
                      setFiatFocused(true)
                    }}
                    onBlur={() => {
                      setFiatFocused(false)
                    }}
                  />
                  <img
                    src='/assets/images/coin_wallet.png'
                    alt='Coin'
                    className='absolute left-4 top-1/2 my-auto h-5 w-5 object-contain'
                    style={{
                      transform: 'translateY(-50%)'
                    }}
                  />
                </div>
                {fiatExceedsMax && (
                  <ErrorMessage
                    className='mt-1 ml-2 flex text-[var(--red)]'
                    message={fiatMaxErrorMessage}
                  />
                )}
              </div>
              <span className='flex h-12 items-center px-4 text-white'>=</span>
              <div className='w-full'>
                <div className='relative'>
                  <input
                    type='number'
                    step='any'
                    className={`${FORM_INPUT} ${errors?.amount && errors.amount.type !== 'max' ? 'border-[var(--red)]' : ''}`}
                    id='amount'
                    placeholder={t('topHeaderAmount')}
                    name='amount'
                    onKeyDown={blockInvalidChar}
                    {...register('amount')}
                  />
                  {selectedWithdrawWallet?.currency?.image && !cryptoImgError && !isUsdt
                    ? (
                      <img
                        src={selectedWithdrawWallet?.currency?.image}
                        alt='Coin'
                        className='absolute left-4 top-1/2 my-auto h-5 w-5 object-contain'
                        style={{
                          transform: 'translateY(-50%)'
                        }}
                        onError={() => setCryptoImgError(true)}
                      />
                      )
                    : (
                      <span
                        className='absolute left-4 top-1/2 my-auto h-5 w-5 [&_svg]:h-5 [&_svg]:w-5' style={{
                          transform: 'translateY(-50%)'
                        }}
                      >
                        <CryptoLogo code={selectedWithdrawWallet?.currency?.code} name={selectedWithdrawWallet?.currency?.name} size={20} />
                      </span>
                      )}
                </div>
                {errors && errors.amount && errors.amount.type !== 'max' && (
                  <ErrorMessage
                    className='mt-1 ml-2 flex text-[var(--red)]'
                    message={errors.amount.message}
                  />
                )}
              </div>
              <div className='max-md:mt-4'>
                <button
                  type='submit'
                  className={`${BTN_SECONDARY} ml-4 h-12 leading-4 max-md:mb-4 max-md:ml-0 max-md:w-full`}
                  disabled={loading}
                >
                  {loading
                    ? <Loader variant={LOADER_HANDLER_TYPES.submit} />
                    : <span className='p-0'>{t('request')}</span>}
                </button>
              </div>
            </div>
          </div>
          <ul className='mb-0 mt-[1.2rem] list-disc pl-5'>
            <li className='text-[0.865rem] font-medium'>
              {t('receiveTheSpecifiedAmount', {
                currency: selectedWithdrawWallet?.currency?.name
              })}
            </li>
            <li className='text-[0.865rem] font-medium'>{t('valueMayVary')}</li>
            <li className='text-[0.865rem] font-medium'>{t('withdrawalFeesMayApply')}</li>
          </ul>
        </div>
      </form>
      {showPinModal && (
        <WithdrawTransactionPasswordModal
          hasTransactionPassword={Boolean(user?.hasTransactionPassword)}
          onClose={() => setShowPinModal(false)}
          onVerified={handlePinVerified}
          onGoToSettings={handleGoToSettings}
        />
      )}
      {showConfirmModal && pendingWithdraw && (
        <WithdrawConfirmModal
          amount={pendingWithdraw.withdrawalAmount}
          currencyName={currencyName}
          address={pendingWithdraw.withdrawalAddress}
          loading={loading}
          onCancel={() => setShowConfirmModal(false)}
          onConfirm={handleConfirmWithdraw}
        />
      )}
    </div>
  )
}

export default WithdrawCurrencySection
